import {
    R6PayApiErrorResponse,
    R6PayApiSuccessResponse,
} from '@r6digital/r6pay-types/types/Api';
import PaymentMethod from '@r6digital/r6pay-types/types/PaymentMethod';
import {
    PaymentProcessor,
    paymentProcessorDisplayName,
} from '@r6digital/r6pay-types/types/PaymentProcessor';
import {
    Action,
    CreatePaymentLinkPayload,
    CreatePaymentLinkResponse,
    Mode,
} from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import Location, {
    SupportedCurrencyCodes,
} from '@r6digital/rapidstor-types/react/entities/Location';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { HttpMethod } from '@/types/Http';

import BaseService from '@/features/rapidstor/services/BaseService';

export default class R6PayService extends BaseService {
    r6PayBaseUrl: string;
    rapidstorBaseUrl: string;
    accessToken: string;
    temporaryToken?: string;
    headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    paymentMethods: PaymentMethod[] = [];

    constructor(location: Location, isDevelopment?: boolean) {
        super(location);
        this.r6PayBaseUrl = isDevelopment
            ? 'https://r6-pay.r6staging.com'
            : 'https://pay.r6clients.com';
        this.rapidstorBaseUrl = isDevelopment
            ? 'https://api.rapidstorapp.com'
            : 'https://prod.rapidstorapp.com'
        if (!location.rapidstor.paymentGateways.gateways.r6Pay) {
            throw new Error('R6Pay gateway not configured for this location');
        }
        this.accessToken = location.rapidstor.paymentGateways.gateways.r6Pay.accessToken;
        this.headers.Authorization = `Bearer ${this.accessToken}`;
    }

    /**
     *
     * HELPER OR SERVICE FUNCTIONS
     *
     */
    dollarsToCents(dollars: number | string): number {
        if (typeof dollars === 'number') {
            return Math.round(100 * dollars);
        } else {
            return Math.round(100 * parseFloat(dollars.replace(/[$,]/g, '')));
        }
    }

    buildCreatePaymentLinkPayload(
        reservation: BaseReservation,
        form: Record<string, string>,
        location: Location,
        currency: SupportedCurrencyCodes,
        return_url: string,
        reservationFee?: number
    ): CreatePaymentLinkPayload {
        const amount = reservationFee || (reservation.moveInContext.movein.paymentAmount ?? 0) +
            (reservation.additionalAmount ?? 0);

        const tenant = reservationFee ? {
            email: reservation.email,
            firstName: reservation.firstName,
            lastName: reservation.lastName,
            external_user_id: `${reservation.tenantId}`,
        } : {
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            external_user_id: `${reservation.tenantId}`,
        }

        return {
            action: reservationFee ? Action.RESERVATION_WITH_FEE : Action.MOVE_IN,
            amount: this.dollarsToCents(amount),
            payment_details: {
                description: reservationFee ? 'Reservation Fee' : 'Move In Costs',
                external_transaction_reference: `${reservation.tenantId}`,
            },
            sender: {
                email: tenant.email,
                name: `${tenant.firstName} ${tenant.lastName}`,
                external_user_id: `${reservation.tenantId}`,
            },
            mode: Mode.PAYMENT,
            payment_method: PaymentProcessor.FINVERSE,
            payment_method_name: paymentProcessorDisplayName(PaymentProcessor.FINVERSE),
            currency: currency,
            status: 'pending',
            timestamp: new Date(),
            return_url: return_url,
            webhook_url: `${this.rapidstorBaseUrl}/api/v1/r6pay/webhook`,
            metadata: {
                action: reservationFee ? Action.RESERVATION_WITH_FEE : Action.MOVE_IN,
                originUrl: window.location.href,
                corp: location.corpCode,
                site: location.locationCode,
                channel: `RapidStor ${reservationFee ? 'Reservation-With-Fee' : 'Move-In'} (V2)`,
                location: location.locationCode,
                locationHash: window.location.hash,
                tenant: `${reservation.tenantId}`,
                tenantId: `${reservation.tenantId}`,
                unique_reference_id: `${reservation.tenantId}_${Date.now() / 1000}`,
                reservationId: `${reservation._id}`,
                frontend_url: window.location.href,
                payment_details: {
                    description: reservationFee ? 'Reservation Fee' : 'Move In Costs',
                    external_transaction_reference: `${reservation.tenantId}`,
                },
                return_url: return_url,
                payment_metadata: {
                    sCorpCode: location.corpCode,
                    sLocationCode: location.locationCode,
                    friendlyName: location.name,
                    SiteId: `${location.locationId}`,
                    tenantId: `${reservation.tenantId}`,
                    unique_reference_id: `${reservation.tenantId}_${Date.now() / 1000}`,
                    channel: `RapidStor ${reservationFee ? 'Reservation-With-Fee' : 'Move-In'} (V2)`,
                },
                webhook_url: `${this.rapidstorBaseUrl}/api/v1/r6pay/webhook`,
            },
        };
    }

    async getPaymentMethods() {
        if (this.paymentMethods.length) {
            return this.paymentMethods;
        }

        const response = await fetch(`${this.r6PayBaseUrl}/api/account/payment_methods`, {
            method: HttpMethod.GET,
            headers: this.headers,
        });

        if (!response.ok) {
            throw new Error('Failed to get payment methods from R6Pay', {
                cause: response,
            });
        }

        const result = (await response.json()) as R6PayApiSuccessResponse<
            PaymentMethod[]
        >;

        this.paymentMethods = result.data;

        return result.data;
    }

    async createPaymentLink(payload: CreatePaymentLinkPayload) {
        const response = await fetch(`${this.r6PayBaseUrl}/api/payment/link/create`, {
            method: HttpMethod.POST,
            headers: this.headers,
            body: JSON.stringify(payload),
        });
        const parsedResponse = await response.json();

        if (!response.ok) {
            const errorResponse = parsedResponse as R6PayApiErrorResponse;
            this.handleError(
                '[R6PayService] Failed to create payment link',
                JSON.stringify(errorResponse.errors),
                [
                    {
                        name: 'external_transaction_reference',
                        value: payload.payment_details.external_transaction_reference,
                    },
                    {
                        name: 'sender',
                        value: JSON.stringify(payload.sender),
                    },
                    {
                        name: 'tenant',
                        value: payload.metadata.tenant,
                    },
                ]
            );
        }

        const result = parsedResponse as R6PayApiSuccessResponse<CreatePaymentLinkResponse>;

        return result.data;
    }
}
